import { useState, useEffect } from "react";
import styled from "styled-components";
import { Redirect, useLocation } from "react-router-dom";
import { parseUrl } from "query-string";

import logo from "../../assets/images/logo.png";
import useAuth from "../../hooks/useAuth";
import Button from "../Button/Button";
import { validate as validateEmail } from "isemail";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;

  background-color: var(--color-bg-sidebar);
  padding: 20px;
  border-radius: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--color-text-primary);
`;

const Input = styled.input`
  border: ${(p) => (!p.isValid ? "1px solid var(--color-error)" : "1px solid var(--color-bg-sidebar)")};
  outline-color: ${(p) => !p.isValid && "var(--color-error)"};
  padding: 8px 15px;
  width: 100%;
  border-radius: 5px;
  color: var(--color-text-primary);
  background-color: var(--color-bg-main);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const Box = styled.div`
  margin: 0 auto;
  padding: 20px;
  width: 100%;
  max-width: 600px;
  color: var(--color-text-primary);

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.header`
  margin-top: 20px;
  margin-bottom: 44px;
  width: 75%;
`;

const Main = styled.main`
  margin-top: 44px;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-secondary);

  button {
    margin-left: auto;
  }
`;

const Heading = styled.h1`
  margin-right: auto;
  color: var(--color-text-primary);
  display: inline-block;
  padding-bottom: 20px;
  font-size: 42px;
  font-weight: 400;
  line-height: 0.8;
  position: relative;

  &::after {
    content: "";
    width: 90px;
    height: 3px;
    background-color: var(--color-primary);
    position: absolute;
    top: 100%;
    left: 0;
    transform: translateY(-100%);
  }
`;

const Logo = styled.figure`
  width: 100%;
`;

const initialState = {
  email: "",
  isValid: true,
  complete: false,
  loading: false,
  error: undefined,
};

const Welcome = () => {
  const location = useLocation();
  const { isAuthenticated, changePassword } = useAuth();
  const [state, setState] = useState(initialState);
  const [parsedEmail, setParsedEmail] = useState(null);

  const handleKeyCheck = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  useEffect(() => {
    document.addEventListener("keyup", handleKeyCheck);
    return () => document.removeEventListener("keyup", handleKeyCheck);
    // eslint-disable-next-line
  }, [state.email]);

  useEffect(() => {
    const {
      query: { useremail },
    } = parseUrl(location.search?.replaceAll(/\+/g, "%2B"));
    setParsedEmail(useremail);
    // eslint-disable-next-line
  }, []);

  const checkIsValidEmail = (email) => checkEmail(email, parsedEmail);
  const handleChange = (e) =>
    setState((state) => ({ ...state, email: e.target.value, isValid: true }));
  const onPasswordError = (err) =>
    setState((state) => ({
      ...state,
      loading: false,
      error: err?.message,
      complete: !err,
    }));

  const handleSubmit = () => {
    if (!checkIsValidEmail(state.email))
      return setState((state) => ({ ...state, isValid: false }));

    setState((state) => ({ ...state, loading: true, isValid: true }));
    changePassword(
      { connection: "Harbor", email: state.email },
      onPasswordError
    );
  };

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <Box>
      <Header>
        <Logo>
          <img src={logo} alt="brand-logo" style={{ width: '100%' }} />
        </Logo>
      </Header>
      <Heading>Welcome to Caylent!</Heading>
      <Main>
        {!state.complete && (
          <p>
            Verify your email and generate a link to setup your password by
            entering your email address below: (Be sure to double check the
            email you enter matches the one we emailed you with, as opposed to
            an alias.). If you have any questions about this contact either your
            organization's Caylent lead, or the Caylent lead assigned to your
            organization.
          </p>
        )}
        {!state.complete && (
          <Wrapper>
            <Label>
              Email
              <Input
                onChange={handleChange}
                isValid={state.isValid}
                type="email"
                placeholder="Email"
              />
            </Label>
            <Button onClick={handleSubmit}>
              {state.loading ? "Verifying..." : "Verify"}
            </Button>
          </Wrapper>
        )}
        {(state.complete || state.error) && (
          <Wrapper>
            <p>
              If correct, an email has been sent to the account you just
              verified. Follow the link contained in that message to set your
              password.
            </p>
          </Wrapper>
        )}
      </Main>
    </Box>
  );
};

function checkEmail(email, parsedEmail) {
  return validateEmail(email) && email === parsedEmail;
}

export default Welcome;
